import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid2,
  SelectChangeEvent,
  Switch,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
  Tabs,
  Tab,
} from "@mui/material";
import React, { useContext, useEffect, useState } from "react";
import { LoginUserInfo } from "IF/interfaceType";
import { API_BASE_URL, RESPONSE_CODE } from "Utils/apiUtil";
import axios from "axios";
import { MessageModal } from "./MessageModal";
import { UserRole, useSessionStorage } from "IF/userSession";
import SettingUserTable from "./SettingUserTable";
import ConfirmationModal from "./ConfirmationModal";
import SettingUserMobile from "./SettingUserMobile";
import { DataContext } from "Contexts/DataProvider";
import { createTitle } from "Utils/format";
export const SettingUser = () => {
  // ユーザ情報
  const { getUserData } = useSessionStorage();
  const userData = getUserData();
  const { id: userId, companyCode, userRole } = userData;

  // サイドバーデータ持ち回り
  const sidebarContext = useContext(DataContext);
  if (!sidebarContext) {
    throw new Error("ChildComponent must be used within an ViewProvider");
  }
  const { menuItems, setChoiceMenu, toggleMenu } = sidebarContext;

  const theme = useTheme();
  const isMd = useMediaQuery(theme.breakpoints.down("md"));

  // 新規登録モーダル開閉
  const [open, setOpen] = useState(false);
  const initData: LoginUserInfo = {
    userId: "",
    userPassword: "",
    adminFlg: false,
    createDatetime: new Date(),
    createUser: userId,
    updateUser: userId,
  };
  // 登録用
  const [newLoginUser, setNewLoginUser] = useState<LoginUserInfo>(initData);
  // 更新/削除用
  const [editLoginUser, setEditLoginUser] = useState<LoginUserInfo>(initData);
  // 更新/削除用(レンタル)
  const [editLoginUserRental, setEditLoginUserRental] =
    useState<LoginUserInfo>(initData);
  // 登録済ユーザリスト
  const [allLoginUser, setAllLoginUser] = useState<LoginUserInfo[]>([]);
  // 登録済レンタルユーザリスト
  const [allLoginUserRental, setAllLoginUserRental] = useState<LoginUserInfo[]>(
    []
  );
  // レイアウト設定
  const [tableBodyHeight, setTableBodyHeight] = useState(600);
  const [cardBodyWidth, setCardBodyWidth] = useState(0);
  // モーダル
  const [isModalOpen, setModalOpen] = useState(false);
  const [message, setMessage] = useState("");
  const closeModal = () => {
    setModalOpen(false);
  };

  // 確認モーダル
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [confirmMessage, setConfirmMessage] = useState("");
  const [onConfirm, setOnConfirm] = useState<(() => void) | null>(null);
  const handleConfirmModalClose = () => setConfirmModalOpen(false);

  const handleConfirmModalOpen = (
    message: string,
    confirmAction: () => void
  ) => {
    setConfirmMessage(message);
    setOnConfirm(() => confirmAction);
    setConfirmModalOpen(true);
  };

  const handleDeleteConfirmModalOpen = (
    index: number,
    message: string,
    confirmAction: () => void
  ) => {
    if (
      allLoginUser.filter((user) => user.adminFlg === true).length === 1 &&
      allLoginUser[index].adminFlg === true
    ) {
      setModalOpen(true);
      setMessage("すべての管理者ユーザーを削除することはできません。");
      return;
    }
    setConfirmMessage(message);
    setOnConfirm(() => confirmAction);
    setConfirmModalOpen(true);
  };

  // タイトル設定
  useEffect(() => {
    if (menuItems.length > 2) {
      const index = UserRole.YKC !== userRole ? 3 : 4;
      document.title = createTitle(menuItems[index]?.subItems?.[4]?.text ?? "");
      setChoiceMenu(menuItems[index]?.subItems?.[4]?.text ?? "");
      toggleMenu("設定");
    }
  }, [userRole, menuItems, setChoiceMenu, toggleMenu]);

  // 初期表示時処理
  useEffect(() => {
    const handleResize = () => {
      const windowHeight = window.innerHeight - 200;
      const windowWidth = window.innerWidth - 55;
      setTableBodyHeight(windowHeight);
      setCardBodyWidth(windowWidth);
    };

    // 初期表示時とリサイズ時に高さを計算
    handleResize();
    window.addEventListener("resize", handleResize);

    // クリーンアップ
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    // データ取得
    getData(companyCode, userRole);
  }, [companyCode, userRole]);

  // 初期表示時のデータ取得処理
  const getData = async (schema: string, userRole: string) => {
    // YKCユーザまたはレンタルユーザの場合
    if (UserRole.YKC === userRole || UserRole.RENTAL === userRole) {
      try {
        // API呼び出し
        const response = await axios.get(
          `${API_BASE_URL}/UserSetting/GetAllLoginUserRental`,
          {
            params: { schema: schema },
          }
        );
        setAllLoginUserRental(response.data);
      } catch (error) {
        setModalOpen(true);
        setMessage("ユーザーの取得に失敗しました。");
        console.error("ユーザーの取得に失敗しました:", error);
      }
    }
    // レンタルユーザ以外の場合
    if (UserRole.RENTAL !== userRole) {
      try {
        // API呼び出し
        const response = await axios.get(
          `${API_BASE_URL}/UserSetting/GetAllLoginUser`,
          {
            params: { schema: schema },
          }
        );
        setAllLoginUser(response.data);
      } catch (error) {
        setModalOpen(true);
        setMessage("ユーザーの取得に失敗しました。");
        console.error("ユーザーの取得に失敗しました:", error);
      }
    }
  };

  // 新規登録モーダルの開閉
  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    setOpen(false);
    setNewLoginUser(initData);
  };

  // チェックボックスの変更
  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEditLoginUser((prev) => ({
      ...prev,
      adminFlg: prev.adminFlg === true ? false : true,
    }));
  };

  // 編集ボタン押下時の処理
  const handleEdit = (index: number) => {
    setEditLoginUser(allLoginUser[index]);
  };

  // 編集中のデータ変更
  const handleChange = (
    event:
      | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
      | SelectChangeEvent<string>
  ) => {
    const { name, value } = event.target as
      | HTMLInputElement
      | HTMLSelectElement;

    // userId の場合のみ英数字以外の文字を削除
    const sanitizedValue =
      name === "userId" || name === "userPassword"
        ? value.replace(/[^A-Za-z0-9]/g, "")
        : value;

    setEditLoginUser((prev) => ({
      ...prev,
      [name]: sanitizedValue, // マスキング後の値をセット
    }));
  };

  // 編集キャンセル
  const handleCancel = () => {
    setEditLoginUser(initData);
  };

  // 編集ボタン押下時の処理
  const handleEditRental = (index: number) => {
    setEditLoginUserRental(allLoginUserRental[index]);
  };

  // 編集中のデータ変更
  const handleChangeRental = (
    event:
      | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
      | SelectChangeEvent<string>
  ) => {
    const { name, value } = event.target as
      | HTMLInputElement
      | HTMLSelectElement;

    // userId の場合のみ英数字以外の文字を削除
    const sanitizedValue =
      name === "userId" || name === "userPassword"
        ? value.replace(/[^A-Za-z0-9]/g, "")
        : value;

    setEditLoginUserRental((prev) => ({
      ...prev,
      [name]: sanitizedValue,
    }));
  };

  // 編集キャンセル
  const handleCancelRental = () => {
    setEditLoginUserRental(initData);
  };

  // 新規登録処理
  const handleAddUserId = async () => {
    try {
      // API呼び出し
      const response = await axios.post(
        `${API_BASE_URL}/UserSetting/PostLoginUser`,
        {
          schema: companyCode,
          loginUserData: newLoginUser,
        }
      );
      if (response.status === RESPONSE_CODE.SUCCESS) {
        setModalOpen(true);
        setMessage("ユーザー情報が登録されました。");
        handleClose();
        getData(companyCode, userRole);
      } else {
        throw new Error();
      }
    } catch (error: any) {
      setModalOpen(true);
      if (error.status === RESPONSE_CODE.BAD_REQUEST) {
        setMessage(error.response.data);
        console.error("Status code:", error.response.status);
        console.error("Message:", error.response.data);
      } else {
        setMessage("ユーザー情報の登録に失敗しました。");
        console.error("ユーザー情報の登録に失敗しました。", error);
      }
    }
  };

  // 初期データと変更後formを比較
  const getModifiedElements = () => {
    if (allLoginUser && editLoginUser) {
      return allLoginUser.filter((current) => {
        return (
          current.id === editLoginUser.id &&
          (current.userId !== editLoginUser.userId ||
            current.userName !== editLoginUser.userName ||
            current.userPassword !== editLoginUser.userPassword ||
            current.adminFlg !== editLoginUser.adminFlg)
        );
      });
    } else {
      return [];
    }
  };

  // 更新時処理
  const handleUpdate = async () => {
    const modifiedElements = getModifiedElements();

    // 変更なしの場合更新しない
    if (modifiedElements.length === 0) {
      setModalOpen(true);
      setMessage("変更がありません。");
      return;
    }

    // ユーザIDを設定
    modifiedElements.map((modifiedItem) => ({
      ...modifiedItem,
      updateUser: userId,
    }));

    // ユーザー情報更新
    try {
      const response = await axios.put(
        `${API_BASE_URL}/UserSetting/PutLoginUser`,
        {
          schema: companyCode,
          loginUserData: editLoginUser,
        }
      );
      if (response.status === RESPONSE_CODE.SUCCESS) {
        setModalOpen(true);
        setMessage("ユーザー情報が更新されました。");
        getData(companyCode, userRole);
        setEditLoginUser(initData);
      }
    } catch (error: any) {
      setModalOpen(true);
      if (error.status === RESPONSE_CODE.BAD_REQUEST) {
        setMessage(error.response.data);
        console.error("Status code:", error.response.status);
        console.error("Message:", error.response.data);
      } else {
        setMessage("ユーザー情報の更新に失敗しました。");
        console.error("ユーザー情報の更新に失敗しました。", error);
      }
    }
  };

  // 更新時処理(Rental)
  const handleUpdateRental = async () => {
    try {
      const response = await axios.put(
        `${API_BASE_URL}/UserSetting/PutLoginUserRental`,
        {
          schema: companyCode,
          loginUserData: editLoginUserRental,
        }
      );
      if (response.status === RESPONSE_CODE.SUCCESS) {
        setModalOpen(true);
        setMessage("レンタルユーザー情報が更新されました。");
        getData(companyCode, userRole);
        setEditLoginUserRental(initData);
      }
    } catch (error: any) {
      setModalOpen(true);
      if (error.status === RESPONSE_CODE.BAD_REQUEST) {
        setMessage(error.response.data);
        console.error("Status code:", error.response.status);
        console.error("Message:", error.response.data);
      } else {
        setMessage("レンタルユーザー情報の更新に失敗しました。");
        console.error("レンタルユーザー情報の更新に失敗しました。", error);
      }
    }
  };

  // 削除処理
  const handleDelete = async (index: number) => {
    const deleteData = allLoginUser[index];
    // API呼び出し
    try {
      const response = await axios.delete(
        `${API_BASE_URL}/UserSetting/DeleteLoginUser`,
        {
          headers: { "Content-Type": "application/json" },
          data: {
            schema: companyCode,
            loginUserData: deleteData,
          },
        }
      );
      if (response.status === RESPONSE_CODE.SUCCESS) {
        setModalOpen(true);
        setMessage("ユーザーが削除されました。");
        getData(companyCode, userRole);
      } else {
        throw new Error();
      }
    } catch (error: any) {
      setModalOpen(true);
      if (error.status === RESPONSE_CODE.BAD_REQUEST) {
        setMessage(error.response.data);
        console.error("Status code:", error.response.status);
        console.error("Message:", error.response.data);
      } else {
        setMessage("ユーザーの削除に失敗しました。");
        console.error("ユーザーの削除に失敗しました:", error);
      }
    }
  };

  // タブの状態管理 (0: ユーザー, 1: レンタルユーザー)
  const [activeTab, setActiveTab] = useState(0);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  return (
    <>
      <MessageModal
        open={isModalOpen}
        message={message}
        handleClose={closeModal}
      />
      <ConfirmationModal
        open={confirmModalOpen}
        message={confirmMessage}
        handleClose={handleConfirmModalClose}
        onConfirm={onConfirm!}
      />
      {/* 新規登録モーダル */}
      <Dialog open={open} onClose={handleClose}>
        <Box sx={{ backgroundColor: theme.palette.custom.sForm }}>
          <DialogTitle>新規ユーザーを登録</DialogTitle>
          <DialogContent>
            <FormControlLabel
              control={
                <Switch
                  checked={newLoginUser.adminFlg}
                  onChange={(e) =>
                    setNewLoginUser({
                      ...newLoginUser,
                      adminFlg: newLoginUser.adminFlg === true ? false : true,
                    })
                  }
                />
              }
              label="管理者"
            />
            <TextField
              autoFocus
              margin="dense"
              label="ログインID(半角英数字)"
              type="text"
              fullWidth
              variant="outlined"
              value={newLoginUser.userId}
              required
              inputProps={{ maxLength: 16, pattern: "[A-Za-z0-9]*" }}
              onBlur={() =>
                setNewLoginUser({
                  ...newLoginUser,
                  userId: newLoginUser.userId.replace(/[^A-Za-z0-9]/g, ""), // 無効な文字を削除
                })
              }
              onChange={(e) =>
                setNewLoginUser({
                  ...newLoginUser,
                  userId: e.target.value,
                })
              }
            />
            <TextField
              margin="dense"
              label="パスワード(半角英数字)"
              type="text"
              fullWidth
              variant="outlined"
              value={newLoginUser.userPassword}
              required
              inputProps={{ maxLength: 16 }}
              onBlur={() =>
                setNewLoginUser({
                  ...newLoginUser,
                  userPassword: newLoginUser.userPassword.replace(
                    /[^A-Za-z0-9]/g,
                    ""
                  ),
                })
              }
              onChange={(e) =>
                setNewLoginUser({
                  ...newLoginUser,
                  userPassword: e.target.value,
                })
              }
            />
            <TextField
              margin="dense"
              label="ユーザー名"
              type="text"
              fullWidth
              variant="outlined"
              value={newLoginUser.userName}
              required
              onChange={(e) =>
                setNewLoginUser({
                  ...newLoginUser,
                  userName: e.target.value,
                })
              }
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose}>キャンセル</Button>
            <Button onClick={handleAddUserId} variant="contained">
              登録
            </Button>
          </DialogActions>
        </Box>
      </Dialog>
      {isMd ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1,
          }}
        >
          {UserRole.YKC === userRole ? (
            <Box sx={{ width: "100%" }}>
              {/* タブとコンテンツのラッパー */}
              <Tabs
                value={activeTab}
                onChange={handleTabChange}
                aria-label="ユーザータイプ選択タブ"
              >
                <Tab label="ユーザー" />
                <Tab label="レンタルユーザー" />
              </Tabs>
              {/* タブに対応するコンテンツ */}
              {/* ユーザー一覧パネル */}
              {activeTab === 0 && (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                    mt: 1,
                  }}
                >
                  {/* 新規登録ボタンを含むヘッダー部分 */}
                  <Box
                    sx={{
                      position: "relative",
                      display: "flex",
                      alignItems: "center",
                      height: 33,
                    }}
                  >
                    <Button
                      variant="contained"
                      onClick={handleOpen}
                      fullWidth
                      sx={{
                        // テキストの右側に配置
                        position: "absolute",
                        right: 0,
                      }}
                    >
                      新規登録
                    </Button>
                  </Box>

                  {/* ユーザー一覧リスト部分（スクロール可能） */}
                  <Box
                    sx={{
                      minHeight: tableBodyHeight,
                      maxHeight: tableBodyHeight,
                      overflowY: "auto",
                    }}
                  >
                    {/* ユーザ一覧テーブル または リスト項目 */}
                    <Grid2 container spacing={1}>
                      {allLoginUser.map((row, index) => (
                        <Grid2 size={{ xs: 12, sm: 6 }} key={row.id}>
                          <SettingUserMobile
                            key={row.id}
                            row={row}
                            index={index}
                            rentalFlg={false}
                            editFormData={editLoginUser}
                            onEdit={handleEdit}
                            handleChange={handleChange}
                            handleCheckboxChange={handleCheckboxChange}
                            handleUpdate={handleUpdate}
                            handleCancel={handleCancel}
                            handleDelete={handleDelete}
                            openModal={handleConfirmModalOpen}
                            width={cardBodyWidth}
                          />
                        </Grid2>
                      ))}
                    </Grid2>
                  </Box>
                </Box>
              )}
              {/* レンタルユーザー一覧パネル */}
              {activeTab === 1 && (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                    mt: 2,
                  }}
                >
                  <Box
                    sx={{
                      minHeight: tableBodyHeight,
                      maxHeight: tableBodyHeight,
                      overflowY: "auto",
                    }}
                  >
                    <Grid2 container spacing={1}>
                      {allLoginUserRental.map((row, index) => (
                        <Grid2 size={{ xs: 12 }} key={row.id}>
                          <SettingUserMobile
                            key={row.id}
                            row={row}
                            index={index}
                            rentalFlg={true}
                            editFormData={editLoginUserRental}
                            onEdit={handleEditRental}
                            handleChange={handleChangeRental}
                            handleUpdate={handleUpdateRental}
                            handleCancel={handleCancelRental}
                            openModal={handleConfirmModalOpen}
                            width={cardBodyWidth}
                          />
                        </Grid2>
                      ))}
                    </Grid2>
                  </Box>
                </Box>
              )}
            </Box>
          ) : (
            UserRole.RENTAL !== userRole && (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 1,
                }}
              >
                <Box
                  sx={{
                    position: "relative",
                    display: "flex",
                    alignItems: "center",
                    height: 33,
                  }}
                >
                  <Button
                    variant="contained"
                    onClick={handleOpen}
                    fullWidth
                    sx={{
                      // テキストの右側に配置
                      position: "absolute",
                      right: 0,
                      mb: 1,
                    }}
                  >
                    新規登録
                  </Button>
                </Box>

                {/* ユーザー一覧リスト部分（スクロール可能） */}
                <Box
                  sx={{
                    minHeight: tableBodyHeight + 70,
                    maxHeight: tableBodyHeight,
                    overflowY: "auto",
                  }}
                >
                  {/* ユーザ一覧テーブル または リスト項目 */}
                  <Grid2 container spacing={1}>
                    {allLoginUser.map((row, index) => (
                      <Grid2 size={{ xs: 12, sm: 6 }} key={row.id}>
                        <SettingUserMobile
                          key={row.id}
                          row={row}
                          index={index}
                          rentalFlg={false}
                          editFormData={editLoginUser}
                          onEdit={handleEdit}
                          handleChange={handleChange}
                          handleCheckboxChange={handleCheckboxChange}
                          handleUpdate={handleUpdate}
                          handleCancel={handleCancel}
                          handleDelete={handleDelete}
                          openModal={handleConfirmModalOpen}
                          width={cardBodyWidth}
                        />
                      </Grid2>
                    ))}
                  </Grid2>
                </Box>
              </Box>
            )
          )}

          {/* レンタルユーザ一覧テーブル */}
          {UserRole.RENTAL === userRole && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 1,
                mt: 2,
              }}
            >
              <Box
                sx={{
                  minHeight: tableBodyHeight,
                  maxHeight: tableBodyHeight,
                  overflowY: "auto",
                }}
              >
                <Grid2 container spacing={1}>
                  {allLoginUserRental.map((row, index) => (
                    <Grid2 size={{ xs: 12 }} key={row.id}>
                      <SettingUserMobile
                        key={row.id}
                        row={row}
                        index={index}
                        rentalFlg={true}
                        editFormData={editLoginUserRental}
                        onEdit={handleEditRental}
                        handleChange={handleChangeRental}
                        handleUpdate={handleUpdateRental}
                        handleCancel={handleCancelRental}
                        openModal={handleConfirmModalOpen}
                        width={cardBodyWidth}
                      />
                    </Grid2>
                  ))}
                </Grid2>
              </Box>
            </Box>
          )}
        </Box>
      ) : (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          {UserRole.RENTAL !== userRole && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 1,
                backgroundColor: theme.palette.custom.userCard,
                borderRadius: 2,
                boxShadow: 3,
                p: 2,
              }}
            >
              {UserRole.YKC === userRole && (
                <Typography fontWeight="fontWeightBold">ユーザー</Typography>
              )}
              {/* ユーザ一覧テーブル */}
              <Box>
                <SettingUserTable
                  rentalFlg={false}
                  role={userRole}
                  data={allLoginUser}
                  editFormData={editLoginUser}
                  onEdit={handleEdit}
                  handleChange={handleChange}
                  handleCheckboxChange={handleCheckboxChange}
                  handleUpdate={handleUpdate}
                  handleCancel={handleCancel}
                  handleDelete={handleDelete}
                  openModal={handleDeleteConfirmModalOpen}
                  tableBodyHeight={
                    UserRole.YKC === userRole
                      ? tableBodyHeight - 190
                      : tableBodyHeight
                  }
                />
              </Box>
              {/* 新規登録ボタン */}
              <Box
                sx={{
                  marginTop: 1.5,
                  textAlign: "end",
                  verticalAlign: "bottom",
                }}
              >
                <Button
                  sx={{ width: "20vh", ml: "auto" }}
                  variant="contained"
                  onClick={handleOpen}
                >
                  新規登録
                </Button>
              </Box>
            </Box>
          )}
          {/* レンタルユーザ一覧テーブル */}
          {(UserRole.YKC === userRole || UserRole.RENTAL === userRole) && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 1,
                backgroundColor: theme.palette.custom.userCardR,
                borderRadius: 2,
                boxShadow: 3,
                p: 2,
              }}
            >
              <Typography fontWeight="fontWeightBold">
                レンタルユーザー
              </Typography>
              <SettingUserTable
                rentalFlg={true}
                role={userRole}
                data={allLoginUserRental}
                editFormData={editLoginUserRental}
                onEdit={handleEditRental}
                handleChange={handleChangeRental}
                handleUpdate={handleUpdateRental}
                handleCancel={handleCancelRental}
                handleDelete={handleDelete}
                tableBodyHeight={90}
              />
            </Box>
          )}
        </Box>
      )}
    </>
  );
};
export default SettingUser;
